import { useCallback, useEffect, useRef, useState } from 'react';

export type PointerDragOffset = {
  x: number;
  y: number;
};

type DragStart = {
  pointerX: number;
  pointerY: number;
  originX: number;
  originY: number;
};

type UsePointerDragOffsetOptions = {
  activationDistancePx?: number;
};

type UsePointerDragOffsetReturn = {
  offset: PointerDragOffset;
  isDragging: boolean;
  isDragSessionActive: boolean;
  onDragHandleMouseDown: (event: React.MouseEvent) => void;
};

const DEFAULT_ACTIVATION_DISTANCE_PX = 8;

export function usePointerDragOffset(
  resetKey: string,
  options: UsePointerDragOffsetOptions = {},
): UsePointerDragOffsetReturn {
  const activationDistancePx = options.activationDistancePx ?? DEFAULT_ACTIVATION_DISTANCE_PX;
  const [offset, setOffset] = useState<PointerDragOffset>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const dragStartRef = useRef<DragStart | null>(null);
  const offsetRef = useRef(offset);
  const isDraggingRef = useRef(false);
  offsetRef.current = offset;
  isDraggingRef.current = isDragging;

  useEffect(() => {
    setOffset({ x: 0, y: 0 });
    setIsDragging(false);
    setIsPointerDown(false);
    dragStartRef.current = null;
    isDraggingRef.current = false;
  }, [resetKey]);

  useEffect(() => {
    if (!isPointerDown) {
      return;
    }

    const handleMouseMove = (event: MouseEvent): void => {
      const start = dragStartRef.current;
      if (!start) {
        return;
      }

      const deltaX = event.clientX - start.pointerX;
      const deltaY = event.clientY - start.pointerY;

      if (!isDraggingRef.current) {
        if (Math.hypot(deltaX, deltaY) < activationDistancePx) {
          return;
        }
        isDraggingRef.current = true;
        setIsDragging(true);
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
      }

      setOffset({
        x: start.originX + deltaX,
        y: start.originY + deltaY,
      });
    };

    const handleMouseUp = (): void => {
      dragStartRef.current = null;
      isDraggingRef.current = false;
      setIsDragging(false);
      setIsPointerDown(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [activationDistancePx, isPointerDown]);

  const onDragHandleMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button !== 0) {
      return;
    }
    event.stopPropagation();
    dragStartRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      originX: offsetRef.current.x,
      originY: offsetRef.current.y,
    };
    setIsPointerDown(true);
  }, []);

  return {
    offset,
    isDragging,
    isDragSessionActive: isPointerDown,
    onDragHandleMouseDown,
  };
}
